import { Add, Search } from '@mui/icons-material';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { DashboardBriefDto, DashboardDto, DashboardMutatePayload } from '@thingslog/repositories';
import React, { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../../components/header';
import useDetermineCompanyId from '../../hooks/useDetermineCompanyId';
import {
  dashboardDataSourcesQueryClient,
  dashboardQueryClient,
} from '../../clients/ReactQueryClients/ReactQueryClients';
import {
  DashboardConfirmationDialog,
  DashboardSelectionList,
  DashboardSettingsForm,
} from './components';
import { useDashboardOperations, useDashboardSelection } from './hooks';

interface Props {}

const getDashboardMutateDefaults = (dashboard: DashboardDto): DashboardMutatePayload => ({
  name: dashboard.name,
  period: dashboard.period,
  colsDesktop: dashboard.colsDesktop,
  colsMobile: dashboard.colsMobile,
  layoutSwitchBreakpointPx: dashboard.layoutSwitchBreakpointPx,
});

const DEFAULT_DASHBOARD_MUTATE_PAYLOAD: DashboardMutatePayload = {
  name: '',
  period: { type: 'predefined', name: 'this-month' },
  colsDesktop: 12,
  colsMobile: 4,
  layoutSwitchBreakpointPx: 768,
};

export const DashboardsPage: FC<Props> = (props: Props) => {
  const { t } = useTranslation();
  const companyId = useDetermineCompanyId();
  const { useDashboard, useDashboards } = dashboardQueryClient;
  const { useDataSources } = dashboardDataSourcesQueryClient;

  const dashboardsQuery = useDashboards(companyId ?? undefined);
  const dataSourcesQuery = useDataSources({
    refetchOnWindowFocus: false,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editDashboardId, setEditDashboardId] = useState<string | null>(null);
  const [deleteDashboard, setDeleteDashboard] = useState<DashboardBriefDto | null>(null);
  const { handleSetDefaultDashboard, handleToggleFavoriteDashboard, handleViewDashboard } =
    useDashboardSelection(companyId);

  const editDashboardQuery = useDashboard(editDashboardId ?? '', companyId ?? undefined, {
    enabled: !!editDashboardId,
    refetchOnWindowFocus: false,
  });

  const dashboards = dashboardsQuery.data ?? [];
  const hasNoDashboards = !dashboardsQuery.isLoading && dashboards.length === 0;

  const {
    handlePersistNewDashboard,
    handlePersistDashboard,
    handleDeleteDashboard,
    isPersistingNewDashboard,
    isPersistingDashboard,
    isDeletingDashboard,
  } = useDashboardOperations(companyId, dataSourcesQuery.data ?? [], hasNoDashboards);

  const filteredDashboards = useMemo((): DashboardBriefDto[] => {
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();

    if (!normalizedSearchTerm) {
      return dashboards;
    }

    return dashboards.filter((dashboard: DashboardBriefDto): boolean =>
      dashboard.name.toLowerCase().includes(normalizedSearchTerm)
    );
  }, [dashboards, searchTerm]);

  const handleRequestDeleteDashboard = (dashboardId: string): void => {
    const dashboard = dashboards.find(
      (dashboardItem: DashboardBriefDto): boolean => dashboardItem.id === dashboardId
    );

    if (dashboard) {
      setDeleteDashboard(dashboard);
    }
  };

  const handleConfirmDeleteDashboard = (): void => {
    if (!deleteDashboard) {
      return;
    }

    handleDeleteDashboard(deleteDashboard.id, { onSuccess: () => setDeleteDashboard(null) });
  };

  const handleCreateDefaultDashboard = (): void => {
    if (!hasNoDashboards) {
      return;
    }

    handlePersistNewDashboard(
      {
        ...DEFAULT_DASHBOARD_MUTATE_PAYLOAD,
        name: t('dashboard_default_dashboard_name', 'Default Dashboard'),
      },
      undefined,
      {
        includeDefaultWidgets: true,
        isDefault: true,
        isFavorite: true,
      }
    );
  };

  return (
    <Header>
      <main className="mx-auto flex w-full max-w-5xl flex-col gap-4 p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <Typography variant="h4" component="h1">
              {t('dashboards_title', 'Dashboards')}
            </Typography>
          </div>
          {!hasNoDashboards && (
            <Button
              variant="contained"
              color="primary"
              disableElevation
              startIcon={<Add />}
              onClick={(): void => setIsCreateDialogOpen(true)}
            >
              {t('dashboard_add_dashboard', 'Add Dashboard')}
            </Button>
          )}
        </div>

        <Paper
          elevation={0}
          className="flex min-w-0 flex-col gap-3 border border-solid border-gray-200 p-3"
        >
          <TextField
            value={searchTerm}
            onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
              setSearchTerm(event.target.value)
            }
            label={t('dashboard_search_dashboards', 'Search dashboards')}
            placeholder={t('dashboard_search_by_name', 'Search by name')}
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <DashboardSelectionList
            dashboards={filteredDashboards}
            variant="page"
            onSelectDashboard={handleViewDashboard}
            onSetDefaultDashboard={handleSetDefaultDashboard}
            onToggleFavoriteDashboard={handleToggleFavoriteDashboard}
            onEditDashboard={setEditDashboardId}
            onDeleteDashboard={handleRequestDeleteDashboard}
            onAddDashboard={(): void => setIsCreateDialogOpen(true)}
            onAddDefaultDashboard={hasNoDashboards ? handleCreateDefaultDashboard : undefined}
            isAddDefaultDashboardDisabled={
              isPersistingNewDashboard || dataSourcesQuery.isLoading || !hasNoDashboards
            }
            emptyTitle={
              hasNoDashboards
                ? t('dashboards_empty_no_dashboards_title', 'No dashboards yet')
                : t('dashboards_empty_no_results_title', 'No dashboards found')
            }
            emptyDescription={
              hasNoDashboards
                ? t(
                    'dashboards_empty_no_dashboards_description',
                    'Start with a default dashboard or create an empty one.'
                  )
                : t(
                    'dashboards_empty_no_results_description',
                    'Try another search or create a new dashboard.'
                  )
            }
          />
        </Paper>
      </main>

      <Dialog
        open={isCreateDialogOpen}
        onClose={(): void => setIsCreateDialogOpen(false)}
        maxWidth="sm"
        disablePortal={true}
        fullWidth
      >
        <DialogTitle>{t('dashboard_add_dashboard', 'Add Dashboard')}</DialogTitle>
        <DialogContent className="p-0">
          <DashboardSettingsForm
            type="create"
            disabled={isPersistingNewDashboard}
            onSubmit={(payload: DashboardMutatePayload): void => {
              handlePersistNewDashboard(payload, {
                onSuccess: () => setIsCreateDialogOpen(false),
              });
            }}
            onCancel={(): void => setIsCreateDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!editDashboardId}
        onClose={(): void => setEditDashboardId(null)}
        maxWidth="sm"
        fullWidth
        disablePortal={true}
      >
        <DialogTitle>{t('dashboard_edit_dashboard', 'Edit Dashboard')}</DialogTitle>
        <DialogContent className="p-0">
          {editDashboardQuery.data ? (
            <DashboardSettingsForm
              type="edit"
              defaultValues={getDashboardMutateDefaults(editDashboardQuery.data)}
              disabled={isPersistingDashboard}
              onSubmit={(payload: DashboardMutatePayload): void => {
                if (editDashboardId) {
                  handlePersistDashboard(editDashboardId, payload, {
                    onSuccess: () => setEditDashboardId(null),
                  });
                }
              }}
              onCancel={(): void => setEditDashboardId(null)}
            />
          ) : (
            <div className="p-4 text-sm text-gray-600">{t('dashboard_loading', 'Loading...')}</div>
          )}
        </DialogContent>
      </Dialog>

      <DashboardConfirmationDialog
        open={!!deleteDashboard}
        title={t('dashboard_delete_dashboard', 'Delete Dashboard')}
        confirmLabel={t('delete', 'Delete')}
        confirmColor="error"
        disabled={isDeletingDashboard}
        onClose={(): void => setDeleteDashboard(null)}
        onConfirm={handleConfirmDeleteDashboard}
      >
        {t('dashboard_delete_confirmation', 'Are you sure you want to delete {{name}}?', {
          name: deleteDashboard?.name ?? t('dashboard_this_dashboard', 'this dashboard'),
        })}
      </DashboardConfirmationDialog>
    </Header>
  );
};
